import { CHEESE_PRICE_BANDS, STANDARD_UNIT_POUNDS } from './pricing';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type ChapterId = 'soft' | 'semi_soft' | 'semi_hard' | 'hard' | 'blue' | 'mystery';
export type ProcessId = 'mozzarella' | 'ricotta' | 'taleggio';

export interface FreshRule {
  ageable: false;
  /** Value multiplier by days held (index = days) */
  freshnessMultipliers: number[];
  /** Discarded at day end once age reaches this many days */
  spoilAfterDays: number;
}

/**
 * Ageable cheeses: value stays at ×1.0 until the cheese-specific minimum age,
 * ramps to the peak multiplier, holds, then decays. Stars never change with age.
 */
export interface AgeRule {
  ageable: true;
  minAgeDays: number;
  peakDays: number;
  peakMult: number;
  holdUntilDays: number;
  decayPerDay: number;
  /** Discarded once aging days reach this */
  spoilAtDays: number;
  /** Needs brine washing on the racks (Taleggio style) */
  washedRind: boolean;
  /** Once out of the cellar, days until it spoils */
  shelfDays: number;
}

export interface CheeseDefinition {
  id: string;
  name: string;
  /** Game level the cheese belongs to (1 = soft cheeses) */
  level: number;
  chapter: ChapterId;
  rarity: Rarity;
  unitLabel: string;
  unitPounds: number;
  /** Explicit key into the fixed Phase 1D price bands */
  marketKey: string;
  aging: FreshRule | AgeRule;
  energyRestore: { base: number; perStar: number };
  /** Production chain implemented in this build, or null when the cheese is codex-only */
  process: ProcessId | null;
  /** Skills that must be owned before a batch can start */
  requiresSkills: string[];
  /** Starts discovered and learned (the family recipe) */
  starter?: boolean;
}

export const CHEESES: Record<string, CheeseDefinition> = {
  mozzarella_fresh: {
    id: 'mozzarella_fresh',
    name: 'Fresh Mozzarella',
    level: 1,
    chapter: 'soft',
    rarity: 'common',
    unitLabel: 'ball',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'mozzarella_fresh',
    aging: { ageable: false, freshnessMultipliers: [1.0, 0.85, 0.6, 0.3], spoilAfterDays: 4 },
    energyRestore: { base: 10, perStar: 4 },
    process: 'mozzarella',
    requiresSkills: ['creamery'],
    starter: true,
  },
  ricotta: {
    id: 'ricotta',
    name: 'Whey Ricotta',
    level: 1,
    chapter: 'soft',
    rarity: 'common',
    unitLabel: 'tub',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'ricotta',
    aging: { ageable: false, freshnessMultipliers: [1.0, 0.9, 0.7, 0.4], spoilAfterDays: 4 },
    energyRestore: { base: 8, perStar: 3 },
    process: 'ricotta',
    requiresSkills: ['creamery'],
  },
  taleggio: {
    id: 'taleggio',
    name: 'Taleggio',
    level: 1,
    chapter: 'soft',
    rarity: 'uncommon',
    unitLabel: 'square',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'taleggio',
    aging: { ageable: true, minAgeDays: 35, peakDays: 45, peakMult: 1.5, holdUntilDays: 60, decayPerDay: 0.03, spoilAtDays: 90, washedRind: true, shelfDays: 7 },
    energyRestore: { base: 14, perStar: 5 },
    process: 'taleggio',
    requiresSkills: ['creamery', 'cellar'],
  },
  gouda: {
    id: 'gouda',
    name: 'Gouda',
    level: 2,
    chapter: 'semi_soft',
    rarity: 'uncommon',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'gouda',
    aging: { ageable: true, minAgeDays: 28, peakDays: 180, peakMult: 1.8, holdUntilDays: 540, decayPerDay: 0.01, spoilAtDays: 720, washedRind: false, shelfDays: 14 },
    energyRestore: { base: 14, perStar: 5 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'press'],
  },
  havarti: {
    id: 'havarti',
    name: 'Havarti',
    level: 2,
    chapter: 'semi_soft',
    rarity: 'uncommon',
    unitLabel: 'loaf',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'havarti',
    aging: { ageable: true, minAgeDays: 60, peakDays: 90, peakMult: 1.4, holdUntilDays: 150, decayPerDay: 0.02, spoilAtDays: 240, washedRind: false, shelfDays: 10 },
    energyRestore: { base: 12, perStar: 4 },
    process: null,
    requiresSkills: ['creamery', 'cellar'],
  },
  cheddar: {
    id: 'cheddar',
    name: 'Cheddar',
    level: 3,
    chapter: 'semi_hard',
    rarity: 'uncommon',
    unitLabel: 'truckle',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'cheddar_farmstead',
    aging: { ageable: true, minAgeDays: 90, peakDays: 365, peakMult: 2.0, holdUntilDays: 730, decayPerDay: 0.005, spoilAtDays: 1000, washedRind: false, shelfDays: 21 },
    energyRestore: { base: 14, perStar: 5 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'press'],
  },
  emmental: {
    id: 'emmental',
    name: 'Emmental',
    level: 3,
    chapter: 'semi_hard',
    rarity: 'rare',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'emmental',
    aging: { ageable: true, minAgeDays: 120, peakDays: 240, peakMult: 1.8, holdUntilDays: 400, decayPerDay: 0.01, spoilAtDays: 600, washedRind: false, shelfDays: 21 },
    energyRestore: { base: 14, perStar: 5 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'press', 'warm_room'],
  },
  parmesan: {
    id: 'parmesan',
    name: 'Parmesan',
    level: 4,
    chapter: 'hard',
    rarity: 'rare',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'parmesan',
    aging: { ageable: true, minAgeDays: 365, peakDays: 730, peakMult: 2.5, holdUntilDays: 1100, decayPerDay: 0.005, spoilAtDays: 1500, washedRind: false, shelfDays: 30 },
    energyRestore: { base: 16, perStar: 6 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'press'],
  },
  pecorino_romano: {
    id: 'pecorino_romano',
    name: 'Pecorino Romano',
    level: 4,
    chapter: 'hard',
    rarity: 'rare',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'pecorino_romano',
    aging: { ageable: true, minAgeDays: 150, peakDays: 270, peakMult: 2.0, holdUntilDays: 450, decayPerDay: 0.005, spoilAtDays: 700, washedRind: false, shelfDays: 30 },
    energyRestore: { base: 16, perStar: 6 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'press', 'sheep'],
  },
  roquefort: {
    id: 'roquefort',
    name: 'Roquefort',
    level: 5,
    chapter: 'blue',
    rarity: 'epic',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'roquefort',
    aging: { ageable: true, minAgeDays: 90, peakDays: 150, peakMult: 2.2, holdUntilDays: 240, decayPerDay: 0.02, spoilAtDays: 330, washedRind: false, shelfDays: 14 },
    energyRestore: { base: 16, perStar: 6 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'sheep', 'blue_cave'],
  },
  gorgonzola: {
    id: 'gorgonzola',
    name: 'Gorgonzola',
    level: 5,
    chapter: 'blue',
    rarity: 'epic',
    unitLabel: 'wheel',
    unitPounds: STANDARD_UNIT_POUNDS,
    marketKey: 'gorgonzola',
    aging: { ageable: true, minAgeDays: 50, peakDays: 80, peakMult: 1.8, holdUntilDays: 120, decayPerDay: 0.02, spoilAtDays: 200, washedRind: false, shelfDays: 10 },
    energyRestore: { base: 16, perStar: 6 },
    process: null,
    requiresSkills: ['creamery', 'cellar', 'blue_cave'],
  },
};

export const CHEESE_IDS = Object.keys(CHEESES);

for (const definition of Object.values(CHEESES)) {
  if (CHEESE_PRICE_BANDS[definition.id]?.marketKey !== definition.marketKey) {
    throw new Error(`Missing or mismatched Phase 1D price band for ${definition.id}`);
  }
}

export function freshnessMultiplier(def: CheeseDefinition, ageDays: number): number {
  if (def.aging.ageable) return 1;
  const table = def.aging.freshnessMultipliers;
  if (ageDays < 0) return 1;
  if (ageDays >= table.length) return 0;
  return table[ageDays];
}

/**
 * Value multiplier for an ageable cheese at a given number of aging days.
 * Flat at 1.0 until the minimum age, linear to the peak, hold, then decay.
 */
export function agingMultiplier(rule: AgeRule, ageDays: number): number {
  if (ageDays < rule.minAgeDays) return 1;
  if (ageDays <= rule.peakDays) {
    const span = Math.max(1, rule.peakDays - rule.minAgeDays);
    return 1 + (rule.peakMult - 1) * ((ageDays - rule.minAgeDays) / span);
  }
  if (ageDays <= rule.holdUntilDays) return rule.peakMult;
  return Math.max(0.5, rule.peakMult - rule.decayPerDay * (ageDays - rule.holdUntilDays));
}

export function agingStageLabel(rule: AgeRule, ageDays: number): string {
  if (ageDays < rule.minAgeDays) return 'unripe';
  if (ageDays < rule.peakDays) return 'ripening';
  if (ageDays <= rule.holdUntilDays) return 'at peak';
  return 'past peak';
}
